import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import TierCard from "@/components/subscription/TierCard";
import UsageMeter from "@/components/subscription/UsageMeter";
import ChangeSubscriptionModal from "@/components/subscription/ChangeSubscriptionModal";
import PageBackButton from "@/components/PageBackButton";
import {
  getSubscriptionTiers,
  getMySubscription,
  getMyUsage,
  changeSubscription,
  cancelSubscription,
} from "@/api/subscriptions";
// payments API used during Stripe checkout flow
// import { createPayment, confirmPayment } from "@/api/payments";
import {
  Crown,
  RefreshCw,
  XCircle,
  BarChart3,
  Loader2,
  Clock,
} from "lucide-react";
import { getTierIconComponent } from "@/components/subscription/tierIcons";

export default function SubscriptionPage() {
  // ── State ─────────────────────────────────────────────────────────
  const [tiers, setTiers] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  // ── Data fetching ─────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tiersData, subData, usageData] = await Promise.all([
        getSubscriptionTiers(),
        getMySubscription(),
        getMyUsage(),
      ]);
      setTiers(tiersData || []);
      setSubscription(subData?.status !== "none" ? subData : null);
      setUsage(
        (usageData?.features || []).filter(
          (feature) => feature?.limit !== null && feature?.limit !== undefined,
        ),
      );
    } catch (err) {
      toast.error("Failed to load subscription data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // ── Handlers ──────────────────────────────────────────────────────
  const handleTierSelect = (tier) => {
    setSelectedTier(tier);
    setShowChangeModal(true);
  };

  const handleConfirmChange = async (tier) => {
    setActionLoading(true);
    try {
      // For now, change directly (free tiers / demo mode).
      // In production with paid tiers, you'd first create a payment intent,
      // redirect to Stripe Checkout, then call changeSubscription with the
      // completed payment_intent_id on return.
      await changeSubscription(tier.code);
      toast.success(`Successfully switched to ${tier.name}!`);
      setShowChangeModal(false);
      setSelectedTier(null);
      try { window.dispatchEvent(new CustomEvent("valpha:subscription-changed")); } catch { /* ignore */ }
      await fetchAll();
    } catch (err) {
      toast.error(err.message || "Failed to change subscription");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    setActionLoading(true);
    try {
      await cancelSubscription(cancelReason);
      toast.success("Subscription canceled. You'll retain access until the grace period ends.");
      setShowCancelConfirm(false);
      setCancelReason("");
      await fetchAll();
    } catch (err) {
      toast.error(err.message || "Failed to cancel subscription");
    } finally {
      setActionLoading(false);
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────
  const statusVariant = (s) => {
    const map = {
      active: "success",
      grace: "warning",
      canceled: "danger",
      past_due: "danger",
      expired: "danger",
    };
    return map[s] || "default";
  };

  const currentTierSort = tiers.find(
    (t) => t.code === subscription?.tier
  )?.sort_order;
  const currentTier = tiers.find((t) => t.code === subscription?.tier) ?? null;
  const CurrentTierIcon = getTierIconComponent(currentTier?.icon);

  // ── Render ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-8">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-start gap-3">
          <PageBackButton label="Back to Settings" to="/settings" preferHistory={false} hideLabelOnMobile />
          <div>
          <h1 className="text-2xl font-bold text-textPrimary flex items-center gap-2">
            <Crown className="w-6 h-6 text-primary" />
            Subscription
          </h1>
          <p className="text-sm text-textSecondary mt-1">
            Manage your plan and feature access
          </p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={fetchAll} title="Refresh">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* ─── Current Plan Banner ─────────────────────────────────── */}
      <Card>
        <CardContent className="p-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <CurrentTierIcon className="w-6 h-6 text-primary" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold text-textPrimary">
                    {subscription
                      ? subscription.tier_name ||
                        subscription.tier?.charAt(0).toUpperCase() +
                          subscription.tier?.slice(1)
                      : "No Active Plan"}
                  </h2>
                  {subscription && (
                    <Badge variant={statusVariant(subscription.status)}>
                      {subscription.status?.charAt(0).toUpperCase() +
                        subscription.status?.slice(1)}
                    </Badge>
                  )}
                </div>
                {subscription?.current_period_end && (
                  <p className="text-sm text-textSecondary mt-0.5">
                    Current period ends{" "}
                    {new Date(subscription.current_period_end).toLocaleDateString(
                      undefined,
                      {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      }
                    )}
                  </p>
                )}
                {subscription?.grace_end_at && subscription.status === "canceled" && (
                  <p className="text-sm text-yellow-400 mt-0.5">
                    Access until{" "}
                    {new Date(subscription.grace_end_at).toLocaleDateString(
                      undefined,
                      {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      }
                    )}
                  </p>
                )}
                {!subscription && (
                  <p className="text-sm text-textSecondary mt-0.5">
                    Choose a plan below to get started
                  </p>
                )}
              </div>
            </div>
            {subscription && subscription.status === "active" && !subscription.is_trial && (
              <Button
                variant="outline"
                size="sm"
                className="text-destructive border-destructive/30 hover:bg-destructive/10"
                onClick={() => setShowCancelConfirm(true)}
              >
                <XCircle className="w-4 h-4 mr-1.5" />
                Cancel Plan
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ─── Trial status callout ────────────────────────────────── */}
      {subscription?.is_trial && (() => {
        const ends = subscription.trial_ends_at || subscription.current_period_end;
        const daysLeft = ends
          ? Math.max(0, Math.ceil((new Date(ends).getTime() - Date.now()) / 86_400_000))
          : null;
        const inGrace = subscription.status === "grace";
        const trialName = subscription.tier_name || "trial";
        return (
          <Card className="border-[#4FA9A1]/40 bg-[#4FA9A1]/5">
            <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-[#4FA9A1] mt-0.5" />
                <div>
                  <div className="text-sm font-semibold text-textPrimary">
                    {inGrace
                      ? "Trial ended — you're in grace period"
                      : daysLeft === 0
                      ? "Your trial ends today"
                      : `Your free ${trialName} trial ends in ${daysLeft} day${daysLeft === 1 ? "" : "s"}`}
                  </div>
                  <div className="text-xs text-textSecondary mt-0.5">
                    Pick a paid plan below to keep access without interruption.
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* ─── Plan Comparison Grid ────────────────────────────────── */}
      <div>
        <h2 className="text-lg font-semibold text-textPrimary mb-4">
          Available Plans
        </h2>
        {tiers.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-textSecondary">
                No subscription plans are currently available. Please check back
                later.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {tiers.map((tier) => (
              <TierCard
                key={tier.code}
                tier={tier}
                currentTierCode={subscription?.tier}
                currentTierSort={currentTierSort}
                onSelect={handleTierSelect}
                loading={actionLoading}
              />
            ))}
          </div>
        )}
      </div>

      {/* ─── Feature Usage Meters ────────────────────────────────── */}
      {usage.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-textPrimary mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            Feature Usage
          </h2>
          <Card>
            <CardContent className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {usage.map((feat) => (
                  <UsageMeter
                    key={feat.feature}
                    label={feat.feature
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (c) => c.toUpperCase())}
                    used={feat.remaining}
                    limit={feat.limit}
                    window={feat.window}
                    windowEnd={feat.window_end}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ─── Change Subscription Modal ───────────────────────────── */}
      <ChangeSubscriptionModal
        open={showChangeModal}
        onClose={() => {
          setShowChangeModal(false);
          setSelectedTier(null);
        }}
        selectedTier={selectedTier}
        currentTier={
          subscription
            ? tiers.find((t) => t.code === subscription.tier)
            : null
        }
        onConfirm={handleConfirmChange}
        loading={actionLoading}
      />

      {/* ─── Cancel Confirmation Modal ───────────────────────────── */}
      {showCancelConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => !actionLoading && setShowCancelConfirm(false)}
          />
          <div className="relative z-10 w-full max-w-md rounded-xl border border-borderDefault bg-backgroundSurface shadow-xl p-6 space-y-4">
            <h3 className="text-lg font-semibold text-textPrimary">
              Cancel Subscription
            </h3>
            <p className="text-sm text-textSecondary">
              Your access will continue until the end of your current billing
              period plus a 7-day grace period. After that, your plan will
              expire.
            </p>
            <div>
              <label className="block text-sm text-textSecondary mb-1.5">
                Reason (optional)
              </label>
              <textarea
                className="w-full rounded-md border border-borderDefault bg-backgroundSurface text-textPrimary p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                rows={3}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Let us know why you're canceling..."
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCancelConfirm(false)}
                disabled={actionLoading}
              >
                Keep Plan
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleCancel}
                disabled={actionLoading}
              >
                {actionLoading ? "Canceling..." : "Confirm Cancellation"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
